import * as Linking from 'expo-linking';
import { Platform } from 'react-native';

import { ILocation } from '@rbi-ctg/frontend';
import { updateAmplitudeATTStatus } from 'state/amplitude/amplitude-att-plugin';
import { requestPushPermission } from 'utils/braze/braze-api';
import {
  PermissionStatus,
  getTrackingPermissionsAsync,
  requestTrackingPermissionsAsync,
} from 'utils/expo-tracking-transparency';
import {
  GeolocationPermissionStates,
  checkIfLocationPermissionIsPreapproved,
  getUsersCurrentPosition,
  promptUserForLocationPermission,
} from 'utils/geolocation';
import logger from 'utils/logger';

export const enablePush = (): void => {
  requestPushPermission();
};

export const openPushSetting = () => {
  if (Platform.OS === 'android') {
    return Linking.openSettings();
  } else if (Platform.OS === 'ios') {
    return Linking.openURL('app-settings:');
  }
  return Promise.resolve();
};

export const enableLocation = (): Promise<ILocation | void> => {
  return checkIfLocationPermissionIsPreapproved().then(state => {
    switch (state) {
      case GeolocationPermissionStates.PROMPT: {
        return new Promise(async (resolve, reject) => {
          try {
            const permissionState = await promptUserForLocationPermission();
            if (permissionState === GeolocationPermissionStates.DENIED) {
              return reject('User denied location permission');
            }
          } catch (error) {
            const message = 'Prompt For Location Permissions error';
            logger.error({ error, message });
            return reject(message);
          }

          return resolve(await getUsersCurrentPosition());
        });
      }
      case GeolocationPermissionStates.GRANTED:
        return getUsersCurrentPosition();
      case GeolocationPermissionStates.DENIED:
      default:
        return;
    }
  });
};

export const appTrackingTransparency = async (): Promise<PermissionStatus | undefined> => {
  try {
    const { granted, canAskAgain, status } = await getTrackingPermissionsAsync();

    if (!granted && canAskAgain) {
      const response = await requestTrackingPermissionsAsync();

      // Update Amplitude att status after we have requested permissions
      updateAmplitudeATTStatus(response.status);

      return response.status;
    }

    return status;
  } catch (error) {
    logger.error({
      error,
      message: 'Error in fetching user App Tracking Transparency status',
      // In legacy implementations, this was always set to true. I'm keeping this here hardcoded
      // just to ensure our data lines up as expected. But it seems like this could go away without
      // any consequences.
      requestPermission: true,
    });
  }
  return undefined;
};
